// @flow
import React from 'react';
import styled from 'styled-components';
import { Icon, Badge, ToolTip } from 'primitive-components';
import { colors } from 'bench-style-constants';

const getSidebarLinkColor = (isActive, isDistinctColor) => {
  if (isActive) return `${colors.white}`;
  if (isDistinctColor) return `${colors.gold}`;
  return `${colors.stone}`;
};

const NavIcon = styled(Icon)`
  height: 24px;
  transition: fill 0.25s ease-out;
  width: 24px;
`;

const StyledButton = styled.button`
  align-items: center;
  background-color: ${colors.charcoal};
  border: none;
  color: ${({ isActive, isDistinctColor }) => getSidebarLinkColor(isActive, isDistinctColor)};
  cursor: pointer;
  display: flex;
  flex: 0 0 100%;
  flex-direction: column;
  font-size: 10px;
  padding: 12px 0;
  position: relative;
  transition: color 0.25s ease-out;

  ${NavIcon} {
    fill: ${({ isActive, isDistinctColor }) => getSidebarLinkColor(isActive, isDistinctColor)};
  }

  &:hover {
    color: ${({ isDistinctColor }) => (isDistinctColor ? colors.medallion : colors.white)};

    ${NavIcon} {
      fill: ${({ isDistinctColor }) => (isDistinctColor ? colors.medallion : colors.white)};
    }
  }

  &:focus {
    color: ${colors.white};
    outline: 0;

    ${NavIcon} {
      fill: ${colors.white};
    }
  }

  ${Badge} {
    left: 36px;
    position: absolute;
    top: 8px;

    @media (min-width: 1441px) {
      top: 8px;
    }
  }

  @media (min-width: 1441px) {
    flex-direction: row;
    padding-left: 20px;
  }
`;

const ButtonText = styled.div`
  display: none;

  @media (min-height: 700px) {
    display: block;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }

  @media (min-width: 1441px) {
    display: none;
  }
`;

const ButtonTextWideSidebar = styled.div`
  display: none;

  @media (min-width: 1441px) {
    display: block;
    font-size: 12px;
    line-height: 16px;
    padding-left: 16px;
  }
`;

const StyledToolTip = styled(ToolTip)`
  @media (min-height: 700px) {
    display: none;
  }

  @media (min-width: 1441px) {
    display: none;
  }
`;

const ToolTipText = styled.div`
  color: white;
  font-size: 12px;
`;

type SidebarButtonProps = {
  testId?: ?string,
  showAlertBadge?: boolean,
  icon: string,
  isActive?: boolean,
  notificationCount?: ?number,
  onClick: () => void,
  text: string,
  expandedText?: ?string,
  isDistinctColor?: boolean,
  heapId?: ?string,
  appcuesElementId?: ?string,
};

const SidebarButton = ({
  testId,
  showAlertBadge,
  icon,
  isActive,
  notificationCount,
  text,
  expandedText,
  isDistinctColor,
  onClick,
  heapId,
  appcuesElementId,
}: SidebarButtonProps) => {
  const showNotificationCount = !!(notificationCount && notificationCount > 0 && !showAlertBadge);

  return (
    <StyledToolTip
      size="small"
      vPosition="center"
      hPosition="right"
      contentComponent={<ToolTipText>{expandedText || text}</ToolTipText>}
    >
      <StyledButton
        id={appcuesElementId}
        data-test-id={testId}
        onClick={onClick}
        isActive={isActive}
        isDistinctColor={isDistinctColor}
        data-heap-id={heapId}
      >
        {showAlertBadge && <Badge icon="bellFilled" />}
        {showNotificationCount && <Badge>{notificationCount}</Badge>}
        <NavIcon size="large" icon={isActive ? `${icon}Filled` : icon} />
        <ButtonText>{text}</ButtonText>
        <ButtonTextWideSidebar>{expandedText || text}</ButtonTextWideSidebar>
      </StyledButton>
    </StyledToolTip>
  );
};

export default SidebarButton;

SidebarButton.defaultProps = {
  testId: null,
  showAlertBadge: false,
  isActive: false,
  notificationCount: null,
  expandedText: null,
  isDistinctColor: false,
  heapId: null,
  appcuesElementId: '',
};
